import { motion } from 'framer-motion';
import { ChevronRight, MessageCircle, Drill, Factory, Fuel, LogIn, History } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../../../context/ThemeContext';

const HowItWorks = () => {
  const navigate = useNavigate();
  const { isDarkTheme } = useTheme();

  const steps = [
    {
      title: "Secure Login",
      icon: <LogIn className="w-8 h-8" />,
      description: "Access your personalized dashboard with secure authentication"
    },
    {
      title: "Choose Model",
      icon: <MessageCircle className="w-8 h-8" />,
      description: "Select from Drilling, Reservoir, or Manufacturing AI experts"
    },
    {
      title: "Intelligent Chat",
      icon: <ChevronRight className="w-8 h-8" />,
      description: "Get instant solutions with domain-specific AI responses"
    },
    {
      title: "History Access",
      icon: <History className="w-8 h-8" />,
      description: "Revisit previous conversations anytime from your archive"
    }
  ];

  return (
    <motion.section 
      id="workflow"
      className={`py-24 px-4 ${
        isDarkTheme 
          ? 'bg-gradient-to-r from-black/90 to-black/80' 
          : 'bg-gradient-to-l from-amber-300/60 to-amber-500'
      }`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
    >
      <div className="max-w-7xl mx-auto relative">
        {/* Floating Elements */}
        <div className={`absolute right-0 top-0 w-32 h-32 ${
          isDarkTheme 
            ? 'bg-amber-900/10' 
            : 'bg-amber-700/10'
        } blur-3xl animate-float`} />
        <div className={`absolute left-0 bottom-0 w-40 h-40 ${
          isDarkTheme 
            ? 'bg-amber-900/10' 
            : 'bg-amber-700/10'
        } blur-3xl animate-float-delayed`} />

        {/* Heading Section */}
        <div className="text-center mb-12 lg:mb-20">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className={`text-3xl lg:text-4xl font-bold mb-4 lg:mb-6 ${
              isDarkTheme 
                ? 'text-amber-400' 
                : 'text-white'
            }`}
          >
            Intelligent Workflow Engine
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className={`max-w-2xl mx-auto text-base lg:text-lg px-4 ${
              isDarkTheme 
                ? 'text-gray-300' 
                : 'text-white/90'
            }`}
          >
            Seamless integration of domain expertise with conversational AI for enhanced petroleum engineering solutions
          </motion.p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-center">
          {/* Process Visualization */}
          <div className="w-full lg:w-1/2">
            {/* Mobile Column Layout */}
            <div className="lg:hidden flex flex-col gap-6">
              {steps.map((step, index) => (
                <motion.div
                  key={index}
                  className={`p-4 lg:p-6 rounded-2xl shadow-xl ${
                    isDarkTheme 
                      ? 'bg-gray-900 text-white' 
                      : 'bg-white text-black'
                  }`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="flex items-center gap-4 mb-4">
                    <div className={`p-3 rounded-xl ${
                      isDarkTheme 
                        ? 'bg-amber-900/20 text-amber-400' 
                        : 'bg-amber-100 text-amber-600'
                    }`}>
                      {step.icon}
                    </div>
                    <h3 className={`text-lg font-semibold ${
                      isDarkTheme 
                        ? 'text-amber-400' 
                        : 'text-amber-600'
                    }`}>
                      {step.title}
                    </h3>
                  </div>
                  <p className={`text-sm ${
                    isDarkTheme 
                      ? 'text-gray-300' 
                      : 'text-gray-600'
                  }`}>{step.description}</p>
                </motion.div>
              ))}
            </div>

            {/* Desktop Circular Layout */}
            <div className="hidden lg:block relative min-h-[500px]">
              <div className="absolute inset-0 flex items-center justify-center">
                <svg className="absolute w-full h-full" viewBox="0 0 100 100">
                  <path
                    d="M50 10 A40 40 0 0 1 90 50 A40 40 0 0 1 50 90 A40 40 0 0 1 10 50 A40 40 0 0 1 50 10"
                    fill="none"
                    stroke={isDarkTheme ? "#4A5568" : "#E0E7FF"}
                    strokeWidth="2"
                    strokeDasharray="5 5"
                  />
                </svg>

                {steps.map((step, index) => (
                  <motion.div
                    key={index}
                    className={`absolute p-6 rounded-2xl shadow-xl w-48 text-center cursor-pointer hover:shadow-2xl transition-all ${
                      isDarkTheme 
                        ? 'bg-gray-900 text-white hover:bg-gray-800' 
                        : 'bg-white text-black hover:bg-amber-50'
                    }`}
                    style={{
                      transform: `rotate(${index * 90}deg) translate(160px) rotate(-${index * 90}deg)`
                    }}
                  >
                    <div className={`mb-4 ${
                      isDarkTheme 
                        ? 'text-amber-400' 
                        : 'text-amber-600'
                    }`}>
                      {step.icon}
                    </div>
                    <h3 className={`font-semibold mb-2 ${
                      isDarkTheme 
                        ? 'text-amber-400' 
                        : 'text-amber-600'
                    }`}>
                      {step.title}
                    </h3>
                    <p className={`text-sm ${
                      isDarkTheme 
                        ? 'text-gray-300' 
                        : 'text-gray-600'
                    }`}>{step.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>

          {/* Interactive Demo Preview */}
          <div className="w-full lg:w-1/2 space-y-8">
            <motion.div 
              className={`rounded-2xl p-6 shadow-xl border ${
                isDarkTheme 
                  ? 'bg-gray-900 border-gray-800 text-white' 
                  : 'bg-white border-gray-100 text-black'
              }`}
              initial={{ x: 50, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
            >
              <div className="flex gap-4 mb-6">
                <button className={`p-3 rounded-xl ${
                  isDarkTheme 
                    ? 'bg-amber-900/20 text-amber-400' 
                    : 'bg-amber-100 text-amber-600'
                }`}>
                  <Drill className="w-6 h-6" />
                </button>
                <button className={`p-3 rounded-xl ${
                  isDarkTheme 
                    ? 'bg-gray-800 text-gray-400' 
                    : 'bg-gray-100 text-gray-400'
                }`}>
                  <Fuel className="w-6 h-6" />
                </button>
                <button className={`p-3 rounded-xl ${
                  isDarkTheme 
                    ? 'bg-gray-800 text-gray-400' 
                    : 'bg-gray-100 text-gray-400'
                }`}>
                  <Factory className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div className={`p-4 rounded-xl ${
                  isDarkTheme 
                    ? 'bg-amber-900/20 text-amber-400' 
                    : 'bg-amber-100 text-amber-600'
                }`}>
                  <p className="text-sm">How can I optimize drilling torque in shale formations?</p>
                </div>
                <div className={`p-4 rounded-xl animate-pulse ${
                  isDarkTheme 
                    ? 'bg-gray-800' 
                    : 'bg-gray-100'
                }`}>
                  <div className={`h-4 rounded w-3/4 mb-2 ${
                    isDarkTheme 
                      ? 'bg-gray-700' 
                      : 'bg-gray-200'
                  }`} />
                  <div className={`h-4 rounded w-full ${
                    isDarkTheme 
                      ? 'bg-gray-700' 
                      : 'bg-gray-200'
                  }`} />
                </div>
              </div>
            </motion.div>

            <motion.button
              onClick={() => navigate('/auth')}
              className={`w-full py-4 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${
                isDarkTheme 
                  ? 'bg-amber-900 text-amber-400 hover:bg-amber-800' 
                  : 'bg-gradient-to-r from-amber-500 to-amber-600 text-white hover:from-amber-500 hover:to-amber-700'
              }`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <LogIn className="w-5 h-5" />
              Start AI-Powered Chat Now
            </motion.button>
          </div>
        </div>
      </div>
    </motion.section>
  );
};

export default HowItWorks;